import type { ActivePiece, Board, GameState, PieceType } from './types';
import { tryRotate } from './srs';
import {
  COLS,
  ROWS,
  clearLines,
  collides,
  createBoard,
  lockPiece,
} from './board';
import { mulberry32, SevenBag } from './rng';
import {
  fallInterval,
  HARD_DROP_POINTS,
  levelForLines,
  lineScore,
  LOCK_BONUS,
  LOCK_DELAY_MS,
  MAX_LOCK_RESETS,
  SOFT_DROP_POINTS,
} from './scoring';

export type GameEvent =
  | { type: 'start' }
  | { type: 'move' }
  | { type: 'rotate' }
  | { type: 'softdrop' }
  | { type: 'harddrop' }
  | { type: 'lock' }
  | { type: 'lines'; count: number }
  | { type: 'levelup'; level: number }
  | { type: 'gameover'; score: number };

export interface GameConfig {
  /** Startlevel 1–9. */
  startLevel?: number;
  /** Fester Seed → deterministische Steinfolge (Tests). Sonst zufällig je Spiel. */
  seed?: number;
}

type Listener = (e: GameEvent) => void;

/**
 * Framework-freie, deterministische Spiel-Engine. Die UI treibt sie über
 * diskrete Kommandos und `update(dtMs)`; die Engine kennt weder DOM noch Zeit.
 */
export class Game {
  private board_: Board = createBoard();
  private active_: ActivePiece | null = null;
  private state_: GameState = 'ready';

  private score_ = 0;
  private lines_ = 0;
  private level_ = 1;
  private startLevel_: number;

  private readonly fixedSeed: number | undefined;
  private bag: SevenBag;

  private gravAccum = 0;
  private lockElapsed = 0;
  private lockResets = 0;

  private listeners: Listener[] = [];

  constructor(config: GameConfig = {}) {
    this.startLevel_ = clampLevel(config.startLevel ?? 1);
    this.level_ = this.startLevel_;
    this.fixedSeed = config.seed;
    this.bag = new SevenBag(mulberry32(this.seedForNewGame()));
  }

  // ---- öffentliche Lese-Zugriffe (für Rendering) ----
  get board(): Board { return this.board_; }
  get active(): ActivePiece | null { return this.active_; }
  get state(): GameState { return this.state_; }
  get score(): number { return this.score_; }
  get lines(): number { return this.lines_; }
  get level(): number { return this.level_; }
  get startLevel(): number { return this.startLevel_; }
  get nextType(): PieceType { return this.bag.peek(); }
  get cols(): number { return COLS; }
  get rows(): number { return ROWS; }

  /** Ghost-Piece: tiefste kollisionsfreie Position des aktiven Steins. */
  get ghost(): ActivePiece | null {
    if (!this.active_) return null;
    let g = this.active_;
    for (;;) {
      const moved = { ...g, y: g.y + 1 };
      if (collides(this.board_, moved)) break;
      g = moved;
    }
    return g;
  }

  // ---- Events ----
  on(cb: Listener): () => void {
    this.listeners.push(cb);
    return () => {
      this.listeners = this.listeners.filter((l) => l !== cb);
    };
  }

  private emit(e: GameEvent): void {
    for (const l of this.listeners) l(e);
  }

  // ---- Steuerung ----

  /** START-Taste: kontextabhängig Start / Pause / Weiter / Neustart. */
  startButton(): void {
    switch (this.state_) {
      case 'ready':
      case 'over':
        this.newGame();
        break;
      case 'playing':
        this.pause();
        break;
      case 'paused':
        this.resume();
        break;
    }
  }

  newGame(): void {
    this.board_ = createBoard();
    this.score_ = 0;
    this.lines_ = 0;
    this.level_ = this.startLevel_;
    this.gravAccum = 0;
    this.lockElapsed = 0;
    this.lockResets = 0;
    this.bag = new SevenBag(mulberry32(this.seedForNewGame()));
    this.state_ = 'playing';
    this.emit({ type: 'start' });
    this.spawnNext();
  }

  pause(): void {
    if (this.state_ === 'playing') this.state_ = 'paused';
  }

  resume(): void {
    if (this.state_ === 'paused') this.state_ = 'playing';
  }

  /** Startlevel für kommende Spiele setzen (Optionen). */
  setStartLevel(level: number): void {
    this.startLevel_ = clampLevel(level);
    if (this.state_ === 'ready' || this.state_ === 'over') {
      this.level_ = this.startLevel_;
    }
  }

  moveLeft(): boolean { return this.shift(-1); }
  moveRight(): boolean { return this.shift(1); }
  rotateCW(): boolean { return this.rotate('cw'); }
  rotateCCW(): boolean { return this.rotate('ccw'); }

  /** Soft-Drop: eine Zeile fallen, +1 Punkt bei Erfolg. */
  softDrop(): boolean {
    if (this.state_ !== 'playing') return false;
    if (this.step()) {
      this.score_ += SOFT_DROP_POINTS;
      this.emit({ type: 'softdrop' });
      return true;
    }
    return false;
  }

  /** Hard-Drop: bis zum Aufsetzen fallen (+2/Zeile), sofort einrasten. */
  hardDrop(): void {
    if (this.state_ !== 'playing' || !this.active_) return;
    let piece = this.active_;
    let dropped = 0;
    for (;;) {
      const moved = { ...piece, y: piece.y + 1 };
      if (collides(this.board_, moved)) break;
      piece = moved;
      dropped++;
    }
    this.active_ = piece;
    this.score_ += dropped * HARD_DROP_POINTS;
    this.emit({ type: 'harddrop' });
    this.lockActive();
  }

  /**
   * Zeitschritt. dtMs = vergangene Millisekunden. Wendet Schwerkraft im
   * Level-Takt an und behandelt das Lock-Delay mit Move-Reset.
   */
  update(dtMs: number): void {
    if (this.state_ !== 'playing' || !this.active_) return;

    if (this.isGrounded()) {
      this.gravAccum = 0;
      this.lockElapsed += dtMs;
      if (this.lockElapsed >= LOCK_DELAY_MS) this.lockActive();
      return;
    }

    this.lockElapsed = 0;
    this.gravAccum += dtMs;
    const interval = fallInterval(this.level_);
    while (this.gravAccum >= interval) {
      this.gravAccum -= interval;
      if (!this.step()) break; // aufgesetzt → nächstes update behandelt Lock
    }
  }

  // ---- interne Mechanik ----

  private seedForNewGame(): number {
    if (this.fixedSeed !== undefined) return this.fixedSeed >>> 0;
    return (Math.random() * 0x100000000) >>> 0;
  }

  private spawn(type: PieceType): ActivePiece {
    const x = type === 'O' ? 4 : 3;
    return { type, rotation: 0, x, y: 0 };
  }

  private spawnNext(): void {
    const piece = this.spawn(this.bag.next());
    this.active_ = piece;
    this.gravAccum = 0;
    this.lockElapsed = 0;
    this.lockResets = 0;
    if (collides(this.board_, piece)) {
      this.state_ = 'over';
      this.emit({ type: 'gameover', score: this.score_ });
    }
  }

  private isGrounded(): boolean {
    if (!this.active_) return false;
    return collides(this.board_, { ...this.active_, y: this.active_.y + 1 });
  }

  /** Eine Zeile fallen (Schwerkraft/Soft-Drop). Erfolg → Lock-Timer zurücksetzen. */
  private step(): boolean {
    if (!this.active_) return false;
    const moved = { ...this.active_, y: this.active_.y + 1 };
    if (collides(this.board_, moved)) return false;
    this.active_ = moved;
    this.lockElapsed = 0;
    this.lockResets = 0; // neue, tiefere Zeile → Reset-Zähler zurück
    return true;
  }

  private shift(dx: number): boolean {
    if (this.state_ !== 'playing' || !this.active_) return false;
    const cand = { ...this.active_, x: this.active_.x + dx };
    if (collides(this.board_, cand)) return false;
    this.active_ = cand;
    this.emit({ type: 'move' });
    this.onGroundedMoveReset();
    return true;
  }

  private rotate(dir: 'cw' | 'ccw'): boolean {
    if (this.state_ !== 'playing' || !this.active_) return false;
    const rotated = tryRotate(this.board_, this.active_, dir);
    if (!rotated) return false;
    this.active_ = rotated;
    this.emit({ type: 'rotate' });
    this.onGroundedMoveReset();
    return true;
  }

  /** Bei erfolgreichem Zug am Boden das Lock-Delay zurücksetzen (max. 15×). */
  private onGroundedMoveReset(): void {
    if (this.isGrounded() && this.lockResets < MAX_LOCK_RESETS) {
      this.lockElapsed = 0;
      this.lockResets++;
    }
  }

  private lockActive(): void {
    if (!this.active_) return;
    this.board_ = lockPiece(this.board_, this.active_);
    this.emit({ type: 'lock' });
    this.score_ += LOCK_BONUS;

    const { board, cleared } = clearLines(this.board_);
    this.board_ = board;
    if (cleared > 0) {
      this.score_ += lineScore(cleared, this.level_);
      this.lines_ += cleared;
      this.emit({ type: 'lines', count: cleared });
      const newLevel = levelForLines(this.lines_, this.startLevel_);
      if (newLevel > this.level_) {
        this.level_ = newLevel;
        this.emit({ type: 'levelup', level: newLevel });
      }
    }
    this.spawnNext();
  }
}

function clampLevel(level: number): number {
  if (!Number.isFinite(level)) return 1;
  return Math.max(1, Math.min(9, Math.floor(level)));
}
